import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { API } from '../App';
import axios from 'axios';

const FAQ = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05 });
  const [faqs, setFaqs] = useState([]);
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await axios.get(`${API}/faq`);
        setFaqs(response.data.faqs);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
        // Fallback data
        setFaqs([
          {
            id: "1",
            question: "What does pricing look like?",
            answer: "Every project is different. We begin with a discovery call to understand your needs and provide a customized proposal based on your vision."
          },
          {
            id: "2",
            question: "Do you provide stage design or LED wall setups for live events?",
            answer: "No. Our focus is on professional video, audio, and streaming production. We handle cameras, switching, sound reinforcement, live streaming, and technical direction, but we do not provide stage design, LED walls, lighting décor, or event staging elements. Our role is to ensure your message is captured and broadcast with clarity and excellence."
          },
          {
            id: "3",
            question: "Do I need any technical experience?",
            answer: "No. You don't need to understand cameras, software, or streaming platforms. We guide, manage, and handle the entire production process for you."
          },
          {
            id: "4",
            question: "What equipment do I need to bring?",
            answer: "None. We can provide everything, or work seamlessly with what you already have."
          },
          {
            id: "5",
            question: "Do you have a physical studio?",
            answer: "Our physical studio is currently in development. Right now, we meet you where you are. Offering on-site, remote, and virtual production support from any location."
          },
          {
            id: "6",
            question: "Can you work with my home setup, church, or current space?",
            answer: "Yes. We specialize in transforming existing spaces into polished, broadcast-quality environments without the overwhelm."
          },
          {
            id: "7",
            question: "What services do you offer?",
            answer: "We provide live and virtual production, multi-platform streaming, video recording, re-broadcasting, studio builds, complete tech support, and post-production."
          },
          {
            id: "8",
            question: "Can you help me learn virtual production or improve my own studio?",
            answer: "Absolutely. We believe knowledge should be shared, not withheld. Our goal is to equip you whether you continue partnering with us or step into doing it yourself. We offer guidance, training, and consulting to help you enhance what you already have or build something new from the ground up."
          },
          {
            id: "9",
            question: "Is this only for churches?",
            answer: "No. We serve pastors, ministries, faith-based entrepreneurs, speakers, educators, and content creators."
          },
          {
            id: "10",
            question: "What happens if something goes wrong during a live stream?",
            answer: "That's on us. We monitor, troubleshoot, and manage the technical side in real time so you can stay present and at peace."
          },
          {
            id: "11",
            question: "Do you offer one-time sessions or ongoing support?",
            answer: "Both. Whether you need help for a single broadcast or long-term partnership, we tailor support to your calling and schedule."
          },
          {
            id: "12",
            question: "What makes Kingdom V.I.B.E. different from other studios?",
            answer: "We specialize in faith-based content and understand the unique needs of ministry. Our team isn't just technically skilled, we're partners in your mission to spread your message. Our role is to enhance clarity and excellence, not performance. The message stays central."
          },
          {
            id: "13",
            question: "How do I get started?",
            answer: "Book a consultation. We'll listen to your vision, assess your setup, and create a clear path forward—no pressure, no confusion."
          }
        ]);
      }
    };
    fetchFAQs();
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  // Split FAQs into two columns
  const midPoint = Math.ceil(faqs.length / 2);
  const leftColumnFaqs = faqs.slice(0, midPoint);
  const rightColumnFaqs = faqs.slice(midPoint);

  const FAQItem = ({ faq, index, actualIndex }) => (
    <motion.div
      key={faq.id}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="faq-item"
      data-testid={`faq-item-${actualIndex}`}
    >
      <button
        onClick={() => toggleFAQ(actualIndex)}
        className="w-full flex items-center justify-between py-3 sm:py-4 text-left group"
        data-testid={`faq-question-${actualIndex}`}
      >
        <span className="font-heading text-sm sm:text-base md:text-lg font-bold pr-3 sm:pr-4 group-hover:text-neon-blue transition-colors">
          {faq.question}
        </span>
        <span className={`flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center border transition-all ${
          openIndex === actualIndex 
            ? 'border-neon-blue bg-neon-blue text-obsidian' 
            : 'border-white/20 text-white/50 group-hover:border-white/40'
        }`}>
          {openIndex === actualIndex ? <ChevronUp size={14} className="sm:w-4 sm:h-4" /> : <ChevronDown size={14} className="sm:w-4 sm:h-4" />}
        </span>
      </button>
      
      <motion.div
        initial={false}
        animate={{
          height: openIndex === actualIndex ? 'auto' : 0,
          opacity: openIndex === actualIndex ? 1 : 0
        }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="pb-3 sm:pb-4 text-xs sm:text-sm text-white/60 leading-relaxed" data-testid={`faq-answer-${actualIndex}`}>
          {faq.answer}
        </p>
      </motion.div>
    </motion.div>
  );

  return (
    <section id="faq" className="relative py-16 sm:py-20 md:py-24 lg:py-32 bg-obsidian" data-testid="faq-section" ref={ref}>
      {/* Pink separator line at top */}
      <div className="absolute top-0 left-0 w-full h-[2px]" style={{ background: 'linear-gradient(to right, transparent, #FF0099, transparent)' }} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 sm:mb-10 md:mb-12"
        >
          <span className="font-mono text-sm sm:text-base uppercase tracking-widest text-white mb-3 sm:mb-4 block">
            FAQ
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold uppercase tracking-tight mb-4 sm:mb-6" data-testid="faq-heading">
            Questions? <span className="text-neon-blue">Answers.</span>
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-white/60">
            Everything you need to know before booking your session.
          </p>
        </motion.div>

        {/* FAQ Two Column Layout - Single column on mobile */}
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
          {/* Left Column */}
          <div className="space-y-1 sm:space-y-2">
            {leftColumnFaqs.map((faq, index) => (
              <FAQItem key={faq.id} faq={faq} index={index} actualIndex={index} />
            ))}
          </div>

          {/* Right Column */}
          <div className="space-y-1 sm:space-y-2">
            {rightColumnFaqs.map((faq, index) => (
              <FAQItem key={faq.id} faq={faq} index={index} actualIndex={midPoint + index} />
            ))}
          </div>
        </div>

        {/* Still have questions */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-8 sm:mt-10 md:mt-12 text-center p-4 sm:p-6 md:p-8 bg-obsidian-paper border border-amber-400/20"
        >
          <h3 className="font-heading text-lg sm:text-xl font-bold mb-2 text-amber-400" style={{ textShadow: '0 0 15px rgba(251, 191, 36, 0.3)' }}>Still have questions?</h3>
          <p className="text-sm sm:text-base text-white/50 mb-4 sm:mb-6">We'd love to hear from you. Reach out and let's chat.</p>
          <a 
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              const contactSection = document.querySelector('#contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="btn-neon btn-neon-gold text-sm sm:text-base px-6 py-3" 
            data-testid="faq-contact-cta"
          >
            <span>Contact Us</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
